import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { PageHeader } from "~/components/PageHeader";
import {
    ApiQuery,
    FilterMetadata,
    FilterOperators,
    QueryResponse,
    SortQuery,
} from "~/modules/filters/types";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { NetFlowsData } from "~/modules/fund_flows/api/queryNetFlows";
import { ColumnSpanConfig } from "~/types";
import { Props as SectionHeaderProps } from "~/components/SectionHeader";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { NetFlowsCard } from "~/modules/visualization/components/NetFlowsChart/NetFlowsCard";
import { ASSET_CLASS_IDS } from "~/consts";
import { RealEstateMapVisualization } from "../../components/RealEstateMapVisualization";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { YieldTimeseriesChart } from "~/modules/visualization/components/YieldTimeseriesChart";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import { AssetList } from "~/modules/assets/components/AssetList";
import {
    VisualizationModeCard,
    Props as VisualizationModeCardProps,
} from "~/modules/visualization/components/Timeseries/VisualizationModeCard";
import {
    Props as ScatterChartCardProps,
    ScatterChartCard,
} from "~/modules/visualization/components/ScatterChart/ScatterChartCard";
import {
    TopTransfersCard,
    Props as TopTransfersCardProps,
} from "~/modules/transactions/components/TopTransfersCard";

// // // //

type PieChartByDistributionProps = {
    leagueTableData: LeagueTableTabsByDistributionType;
    title?: string;
    colSpans?: ColumnSpanConfig;
    defaultGroupBy?: LeagueTableGroupTypes;
    allowedGroupByValues?: LeagueTableGroupTypes[];
    groupByLabelOverrides?: Partial<Record<LeagueTableGroupTypes, string>>;
    onChangeGroupBy?: (obj: {
        prevValue: LeagueTableGroupTypes;
        nextValue: LeagueTableGroupTypes;
    }) => void;
};

function PieChartRenderer(props: { pieChart: PieChartByDistributionProps }) {
    const { pieChart } = props;

    return (
        <LeagueTablePieChart
            leagueTableTabs={pieChart.leagueTableData}
            title={pieChart.title}
            colSpans={pieChart.colSpans}
            defaultGroupBy={pieChart.defaultGroupBy}
            allowedGroupByValues={pieChart.allowedGroupByValues}
            onChangeGroupBy={pieChart.onChangeGroupBy}
            groupByLabelOverrides={pieChart.groupByLabelOverrides}
        />
    );
}

export function AssetClassIndividualPage(props: {
    title: string;
    subtitle: React.ReactNode;
    assetsQuery: ApiQuery;
    listQueryResponse: QueryResponse<Asset>;
    netFlows?: {
        title?: string;
        data: NetFlowsData;
    };
    aggregates: Array<AggregateDisplayProps>;
    timeseriesChart?: VisualizationModeCardProps;
    pieChart: PieChartByDistributionProps;
    header: SectionHeaderProps;
    leagueTable: {
        leagueTableData: LeagueTableTabsByDistributionType;
        analyticsTableName: string;
        title?: string;
        colSpans?: ColumnSpanConfig;
    };
    table: {
        columns: Array<ColumnMetadata<AssetTableRowData, AssetColumns>>;
        analyticsTableName: string;
        sortQuery: SortQuery;
        includeAdditionalNetworksRow?: boolean;
        filterMetadata: FilterMetadata[];
        exportFilename: string;
    };
    scatterChart?: ScatterChartCardProps;
    topTransfers?: TopTransfersCardProps;
    options?: {
        isRealEstate?: boolean;
    };
}) {
    const {
        listQueryResponse,
        aggregates,
        netFlows,
        // // // //
        title,
        subtitle,
        assetsQuery,
        timeseriesChart,
        pieChart,
        leagueTable,
        header,
        table,
        scatterChart,
        topTransfers,
        options,
    } = props;

    const IS_REAL_ESTATE = options?.isRealEstate ?? false;

    const chartColumnSpan = timeseriesChart?.columnSpanConfig ?? {
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
        xxl: 12,
    };

    return (
        <div className="flex flex-col h-full grow">
            <PageHeader title={title} subtitle={<>{subtitle}</>} />

            <AggregateGrid aggregates={aggregates} />

            <VisualizationGrid>
                {/* TODO - streamline this when we get rid of props.timeseriesChat */}
                {timeseriesChart && (
                    <VisualizationModeCard {...timeseriesChart} />
                )}

                {/* Render real-estate specific visualizations */}
                {IS_REAL_ESTATE && (
                    <>
                        <RealEstateMapVisualization
                            assets={listQueryResponse.results}
                        />

                        <PieChartRenderer pieChart={pieChart} />

                        <HomePageLeagueTable
                            leagueTableTabs={leagueTable.leagueTableData}
                            analyticsTableName={leagueTable.analyticsTableName}
                            title={leagueTable.title}
                            colSpans={leagueTable.colSpans}
                        />
                    </>
                )}

                {/* Render non-real-estate visualizations */}
                {!IS_REAL_ESTATE && (
                    <>
                        {chartColumnSpan?.xxl === 12 ? (
                            <>
                                <HomePageLeagueTable
                                    leagueTableTabs={
                                        leagueTable.leagueTableData
                                    }
                                    analyticsTableName={
                                        leagueTable.analyticsTableName
                                    }
                                    title={leagueTable.title}
                                    colSpans={leagueTable.colSpans}
                                />
                                <PieChartRenderer pieChart={pieChart} />
                            </>
                        ) : (
                            <>
                                <PieChartRenderer pieChart={pieChart} />

                                {/* Render YieldTimeSeriesChart for Treasury Funds page */}
                                <FeatureFlag
                                    feature={
                                        FeatureNames.enableExperimentalFeatures
                                    }
                                >
                                    <YieldTimeseriesChart
                                        groupBy={
                                            AssetTimeseriesGroupBy.assetClass
                                        }
                                        analyticsChartName="treasuries-yield-timeseries"
                                        filters={[
                                            {
                                                field: AssetTimeseriesColumns.assetClassID,
                                                operator:
                                                    FilterOperators.Equals,
                                                value: ASSET_CLASS_IDS.TreasuryProduct,
                                            },
                                        ]}
                                        columnSpanConfig={{
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                            xl: 12,
                                            xxl: 12,
                                        }}
                                    />
                                </FeatureFlag>

                                <HomePageLeagueTable
                                    leagueTableTabs={
                                        leagueTable.leagueTableData
                                    }
                                    analyticsTableName={
                                        leagueTable.analyticsTableName
                                    }
                                    title={leagueTable.title}
                                    colSpans={leagueTable.colSpans}
                                />
                            </>
                        )}
                    </>
                )}

                {netFlows && (
                    <NetFlowsCard
                        netFlows={netFlows.data}
                        title={netFlows.title}
                    />
                )}

                {scatterChart && <ScatterChartCard {...scatterChart} />}

                {topTransfers && <TopTransfersCard {...topTransfers} />}
            </VisualizationGrid>

            <div className="mt-6">
                <AssetList
                    title={header.label}
                    initialState={listQueryResponse}
                    filterMetadata={table.filterMetadata}
                    columns={table.columns}
                    enableExport={true}
                    exportFilename={table.exportFilename}
                    enableApiTools={true}
                    analyticsTableName={table.analyticsTableName}
                    includeAdditionalNetworksRow={
                        table.includeAdditionalNetworksRow
                    }
                    assetsQuery={{
                        ...assetsQuery,
                        // TODO - hoist this pagination into the assetsQuery itself, no need to override here
                        pagination: {
                            page: 1,
                            perPage: 25,
                        },
                    }}
                />
            </div>
        </div>
    );
}
